import { useEffect, useState } from "react";
import { ClipboardCheck, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

// Founder + BDOM dashboard tile listing every unreviewed B2B/B2C deck,
// oldest first so nothing stays buried for weeks. Each row has a quick
// "Mark reviewed" button that stamps the sign-off inline — the tile
// then shrinks live so the queue empties visibly.
export default function ReviewQueueTile() {
  const [data, setData] = useState({ count: 0, items: [] });

  // Defensive normaliser — production caches / older backends may return
  // a partial payload (e.g. `{count: 5}` without `items`). Any missing
  // field is filled with a safe default so the render can never throw.
  const normalise = (d) => ({
    count: Number(d?.count) || 0,
    items: Array.isArray(d?.items) ? d.items : [],
  });

  const load = () => api.get("/staff-hub/review-queue")
    .then(r => setData(normalise(r.data)))
    .catch(() => setData({ count: 0, items: [] }));
  useEffect(() => { load(); }, []);

  const markReviewed = async (it) => {
    try {
      await api.post(`/staff-hub/items/${it.id}/toggle-review`);
      toast.success("Marked reviewed");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const catLabel = { b2b_docs: "B2B", b2c_docs: "B2C" };

  if (data.count === 0) {
    return (
      <div className="aii-card p-5 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="review-queue-tile">
        <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1"><ClipboardCheck size={11}/> B2B / B2C review queue</div>
        <h3 className="font-display font-semibold text-lg mt-0.5">All caught up ✓</h3>
        <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Every deck has been signed off. New uploads land here automatically.</p>
      </div>
    );
  }

  return (
    <div className="aii-card p-5 bg-gradient-to-br from-amber-50 to-white border-amber-200" data-testid="review-queue-tile">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-800 font-bold flex items-center gap-1"><ClipboardCheck size={11}/> B2B / B2C review queue</div>
          <h3 className="font-display font-semibold text-lg mt-0.5">{data.count} deck{data.count === 1 ? "" : "s"} awaiting sign-off</h3>
        </div>
        <a href="/staff-hub" className="text-xs px-3 py-1.5 bg-white border border-amber-300 text-amber-800 rounded-md hover:bg-amber-100 font-semibold flex items-center gap-1"><ExternalLink size={11}/> Open Staff Hub</a>
      </div>
      <ul className="mt-3 divide-y divide-amber-100">
        {(data.items || []).slice(0, 5).map(it => (
          <li key={it.id} data-testid={`rq-row-${it.id}`} className="py-2 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[9px] uppercase tracking-widest text-amber-700 font-bold">{catLabel[it.category] || "Deck"}</div>
              <div className="text-sm font-medium truncate">{it.title}</div>
              <div className="text-[11px] text-[color:var(--aii-muted)]">Added {it.created_at?.slice(0,10)}{it.created_by_name ? ` · by ${it.created_by_name}` : ""}</div>
            </div>
            <button onClick={() => markReviewed(it)}
                    data-testid={`rq-mark-${it.id}`}
                    className="text-[11px] px-2.5 py-1 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 font-semibold shrink-0">
              ✓ Mark reviewed
            </button>
          </li>
        ))}
        {data.count > 5 && (
          <li className="pt-2 text-[11px] text-[color:var(--aii-muted)]">+ {data.count - 5} more in the queue</li>
        )}
      </ul>
    </div>
  );
}
